import axios from "axios";
import {
  fetchWithCache,
  fetchWithCacheMetadata,
  buildCacheKey,
  isLikelyNetworkError,
} from "../utils/offlineCache";
import {
  enqueueRequest,
  buildTempId,
  getQueueEntriesForCurrentContext,
  isOffline,
} from "../utils/offlineQueue";
import { cacheSet } from "../utils/offlineStore";

// URL de base pour les appels API
const BASE_URL = process.env.REACT_APP_API_URL || "";
const PRODUCTION_DEVICE_ID_STORAGE_KEY = "elsa_production_offline_device_id";
const PRODUCTION_OFFLINE_BOOTSTRAP_UNIT_LIMIT = 300;
const MAX_PRODUCTION_OUTPUT_QUANTITY = 999999.99;

const getOfflineProductionDeviceId = () => {
  if (typeof window === "undefined") {
    return "server";
  }

  const existingId = window.localStorage.getItem(
    PRODUCTION_DEVICE_ID_STORAGE_KEY
  );
  if (existingId) {
    return existingId;
  }

  const nextId = `production-device-${Date.now()}-${Math.random()
    .toString(36)
    .slice(2, 10)}`;
  window.localStorage.setItem(PRODUCTION_DEVICE_ID_STORAGE_KEY, nextId);
  return nextId;
};

const roundOfflineNumber = (value, precision = 2) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return 0;
  }

  return Number(numericValue.toFixed(precision));
};

const floorOfflineCapacity = (value, step = 0.01) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue) || numericValue <= 0) {
    return 0;
  }

  return roundOfflineNumber(
    Math.floor((numericValue + 1e-9) / step) * step,
    4
  );
};

const buildQueuedProductionHistoryRecord = (entry) => {
  const payload = entry?.body || {};
  const preview = entry?.meta?.preview || {};
  const tempId = entry?.meta?.tempId || `offline-${entry?.id || Date.now()}`;
  const costSummary = preview.cost_summary || {};

  return {
    id: tempId,
    production_code: tempId,
    production_unit_id:
      Number(payload.production_unit_id) || preview.production_unit_id || null,
    production_unit_name:
      preview.production_unit_name ||
      `Unite #${payload.production_unit_id || "inconnue"}`,
    production_kind: preview.production_kind || "finished",
    user_id: payload.user_id || null,
    user_name: preview.user_name || "Hors ligne",
    output_quantity:
      Number(payload.output_quantity) || Number(preview.output_quantity) || 0,
    total_cost: Number(costSummary.total_cost) || 0,
    unit_cost_real: Number(costSummary.unit_cost_real) || 0,
    materials_cost_total: Number(costSummary.total_material_cost) || 0,
    variable_cost_total: Number(costSummary.total_variable_cost) || 0,
    depreciation_cost_total: Number(costSummary.total_depreciation_cost) || 0,
    status: "pending_sync",
    notes: payload.notes || "",
    created_at: new Date(entry?.createdAt || Date.now()).toISOString(),
    updated_at: new Date(entry?.createdAt || Date.now()).toISOString(),
    warehouse_id: Number(payload.warehouse_id) || null,
    company_id: Number(payload.company_id) || null,
    __offline: true,
    __queue_id: entry?.id || null,
  };
};

const getPendingProductionLogsFromQueue = async (filters = {}) => {
  const queue = await getQueueEntriesForCurrentContext();
  const warehouseId = Number(filters?.warehouse_id) || null;
  const companyId = Number(filters?.company_id) || null;

  return (queue || [])
    .filter((entry) => entry?.meta?.type === "production_process")
    .filter((entry) => {
      const entryWarehouseId = Number(entry?.body?.warehouse_id) || null;
      const entryCompanyId = Number(entry?.body?.company_id) || null;

      if (warehouseId && entryWarehouseId !== warehouseId) {
        return false;
      }

      if (companyId && entryCompanyId !== companyId) {
        return false;
      }

      return true;
    })
    .map(buildQueuedProductionHistoryRecord);
};

const buildProductionPreviewFallback = (payload = {}, offlinePreview = null) => {
  const preview = offlinePreview || {};
  const fallbackCostSummary = preview.cost_summary || {};

  return {
    production_unit_id:
      Number(payload.production_unit_id) || preview.production_unit_id || null,
    production_unit_name:
      preview.production_unit_name ||
      `Unite #${payload.production_unit_id || "inconnue"}`,
    production_kind: preview.production_kind || "finished",
    user_name: preview.user_name || "Hors ligne",
    output_quantity:
      Number(payload.output_quantity) || Number(preview.output_quantity) || 0,
    cost_summary: {
      total_material_cost: Number(fallbackCostSummary.total_material_cost) || 0,
      total_variable_cost: Number(fallbackCostSummary.total_variable_cost) || 0,
      total_depreciation_cost:
        Number(fallbackCostSummary.total_depreciation_cost) || 0,
      total_cost: Number(fallbackCostSummary.total_cost) || 0,
      unit_cost_real: Number(fallbackCostSummary.unit_cost_real) || 0,
    },
    materials: Array.isArray(preview.materials) ? preview.materials : [],
    outputs: Array.isArray(preview.outputs) ? preview.outputs : [],
    variable_costs: Array.isArray(preview.variable_costs)
      ? preview.variable_costs
      : [],
    financial_tracking: preview.financial_tracking || null,
  };
};

const buildProductionOfflineBootstrapCacheKey = (params = {}) =>
  buildCacheKey("production:offline-bootstrap", {
    company_id: Number(params?.company_id) || null,
    warehouse_id: Number(params?.warehouse_id) || null,
  });

const getProductionOfflineSnapshotUnits = (bootstrapPayload) =>
  Array.isArray(bootstrapPayload?.offline_snapshot?.units)
    ? bootstrapPayload.offline_snapshot.units
    : [];

const getProductionOfflineSnapshotMeta = (bootstrapPayload) =>
  bootstrapPayload?.offline_snapshot || null;

const filterProductionUnitsFromOfflineBootstrap = (units, params = {}) => {
  const search = String(params?.search || "")
    .trim()
    .toLowerCase();
  const statusFilter =
    params?.status === undefined || params?.status === null || params?.status === ""
      ? null
      : String(params.status).trim().toLowerCase();

  return (units || []).filter((unit) => {
    if (statusFilter) {
      const normalizedStatus = String(unit?.status || "")
        .trim()
        .toLowerCase();
      if (normalizedStatus !== statusFilter) {
        return false;
      }
    }

    if (!search) {
      return true;
    }

    const haystack = [
      unit?.name,
      unit?.description,
      unit?.production_kind,
      unit?.recipe_summary?.version_label,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    return haystack.includes(search);
  });
};

const buildProductionUnitsPayloadFromOfflineBootstrap = (
  bootstrapPayload,
  params = {}
) => {
  const allUnits = filterProductionUnitsFromOfflineBootstrap(
    getProductionOfflineSnapshotUnits(bootstrapPayload),
    params
  );
  const page = Math.max(Number(params?.page) || 1, 1);
  const limit = Math.max(Number(params?.limit) || allUnits.length || 1, 1);
  const startIndex = (page - 1) * limit;
  const paginatedUnits = allUnits.slice(startIndex, startIndex + limit);

  return {
    units: paginatedUnits,
    total: allUnits.length,
    pagination: {
      currentPage: page,
      totalPages: Math.max(Math.ceil(allUnits.length / limit), 1),
      totalItems: allUnits.length,
      itemsPerPage: limit,
    },
    filters: params,
    offline: true,
    cached: true,
    local_bootstrap: true,
    offline_snapshot: getProductionOfflineSnapshotMeta(bootstrapPayload),
  };
};

const primeProductionOfflineBootstrapCaches = async (
  bootstrapPayload,
  params = {}
) => {
  const units = getProductionOfflineSnapshotUnits(bootstrapPayload);
  if (!units.length) {
    return bootstrapPayload;
  }

  const warehouseId = Number(params?.warehouse_id) || null;
  const companyId = Number(params?.company_id) || null;

  await Promise.all(
    units.map((unit) =>
      cacheSet(
        buildCacheKey("production:unit:detail", {
          id: Number(unit?.id) || null,
          company_id: companyId,
          warehouse_id: warehouseId,
        }),
        unit
      )
    )
  );

  return bootstrapPayload;
};

export const prefetchProductionOfflineBootstrap = async (params = {}) => {
  const warehouseId = Number(params?.warehouse_id) || null;
  const companyId = Number(params?.company_id) || null;

  if (!warehouseId || !companyId) {
    return null;
  }

  const bootstrapPayload = await fetchWithCache(
    buildProductionOfflineBootstrapCacheKey({
      company_id: companyId,
      warehouse_id: warehouseId,
    }),
    async () => {
      const response = await axios.get(`${BASE_URL}/api/production/bootstrap`, {
        params: {
          company_id: companyId,
          warehouse_id: warehouseId,
          include_offline_snapshot: 1,
        },
      });
      return response.data;
    },
    null
  );

  if (!bootstrapPayload) {
    return null;
  }

  await primeProductionOfflineBootstrapCaches(bootstrapPayload, {
    company_id: companyId,
    warehouse_id: warehouseId,
  });

  return bootstrapPayload;
};

const getProductionUnitFromOfflineBootstrap = async (id, params = {}) => {
  const bootstrapPayload = await prefetchProductionOfflineBootstrap(params);
  if (!bootstrapPayload) {
    return null;
  }

  const normalizedId = Number(id) || null;
  return (
    getProductionOfflineSnapshotUnits(bootstrapPayload).find(
      (unit) => Number(unit?.id) === normalizedId
    ) || null
  );
};

const hasProductionOfflineSnapshotData = (unitSnapshot) =>
  Array.isArray(unitSnapshot?.materials) && unitSnapshot.materials.length > 0;

const resolveProductionOfflineUnitSnapshot = async (
  unitSnapshot,
  payload = {}
) => {
  if (hasProductionOfflineSnapshotData(unitSnapshot)) {
    return unitSnapshot;
  }

  const productionUnitId =
    Number(unitSnapshot?.id) || Number(payload?.production_unit_id) || null;
  if (!productionUnitId) {
    return unitSnapshot;
  }

  const bootstrapUnit = await getProductionUnitFromOfflineBootstrap(
    productionUnitId,
    payload
  );

  if (!bootstrapUnit) {
    return unitSnapshot;
  }

  if (!unitSnapshot) {
    return bootstrapUnit;
  }

  return {
    ...bootstrapUnit,
    ...unitSnapshot,
    materials: hasProductionOfflineSnapshotData(unitSnapshot)
      ? unitSnapshot.materials
      : bootstrapUnit.materials,
    outputs:
      Array.isArray(unitSnapshot?.outputs) && unitSnapshot.outputs.length > 0
        ? unitSnapshot.outputs
        : bootstrapUnit.outputs,
    variable_costs:
      Array.isArray(unitSnapshot?.variable_costs) &&
      unitSnapshot.variable_costs.length > 0
        ? unitSnapshot.variable_costs
        : bootstrapUnit.variable_costs,
    recipe_summary: unitSnapshot?.recipe_summary || bootstrapUnit.recipe_summary,
    offline_snapshot:
      unitSnapshot?.offline_snapshot || bootstrapUnit.offline_snapshot || null,
  };
};

const buildOfflineProductionGuardrails = ({
  recipeSummary,
  allAvailable,
  stockDataComplete,
  snapshotFresh,
  snapshotMeta,
}) => {
  const warningReasons = [];
  const blockingReasons = [];
  const reasons = [];
  const actions = [];

  if (!snapshotFresh) {
    blockingReasons.push({
      code: "offline_snapshot_stale",
      message:
        "Le snapshot offline de production est trop ancien pour autoriser un lancement hors ligne.",
    });
    reasons.push(
      "Le contexte local de production a expire et doit etre rafraichi depuis le serveur."
    );
    actions.push({
      code: "refresh_bootstrap",
      title: "Reconnecter et recharger le module",
      description:
        "Rechargez le module Production en ligne pour recuperer un snapshot a jour avant tout lancement hors ligne.",
    });
  } else if (!stockDataComplete) {
    blockingReasons.push({
      code: "offline_stock_data_missing",
      message:
        "Les niveaux de stock locaux ne sont pas disponibles pour tous les composants.",
    });
    reasons.push(
      "Le detail local de l'unite ne contient pas encore tous les niveaux de stock necessaires."
    );
    actions.push({
      code: "reconnect_once",
      title: "Reconnecter le poste",
      description:
        "Rechargez une fois cette unite en ligne pour memoriser les stocks utilises par le calcul hors ligne.",
    });
  } else if (!allAvailable) {
    blockingReasons.push({
      code: "offline_stock_insufficient",
      message:
        "Le dernier stock local connu semble insuffisant pour cette quantite.",
    });
    reasons.push(
      "Le stock local mémorisé pour au moins un composant est insuffisant."
    );
    actions.push({
      code: "reduce_quantity",
      title: "Reduire la quantite",
      description:
        "Baissez la quantite a produire ou reconnectez-vous pour verifier le stock reel du serveur.",
    });
  } else {
    warningReasons.push({
      code: "offline_local_estimate",
      message:
        "Le calcul a ete reconstruit localement a partir du dernier detail d'unite memorise.",
    });
    reasons.push(
      "Le calcul est une estimation locale basée sur la configuration de l'unité et le dernier stock connu."
    );
    actions.push({
      code: "sync_validation",
      title: "Laisser le serveur reverifier",
      description:
        "Au retour reseau, la synchronisation recontrolera FIFO, lots et couts avant validation definitive.",
    });
  }

  if (recipeSummary?.modified_recently) {
    warningReasons.push({
      code: "recipe_recently_modified",
      message: "La configuration a été modifiée récemment. Une vérification est conseillée.",
    });
  }

  return {
    status:
      !snapshotFresh || !stockDataComplete || !allAvailable
        ? "blocked"
        : "warning",
    requires_acknowledgement: false,
    warning_reasons: warningReasons,
    blocking_reasons: blockingReasons,
    top_material_drivers: [],
    lot_alerts: [],
    lot_alert_count: 0,
    user_guidance: {
      title:
        !snapshotFresh || !stockDataComplete || !allAvailable
          ? "Calcul hors ligne limite"
          : "Calcul hors ligne reconstruit localement",
      summary:
        !snapshotFresh || !stockDataComplete || !allAvailable
          ? "Le poste ne dispose pas d'un contexte local suffisant pour valider ce lancement en toute securite."
          : "Le poste peut estimer cette production hors ligne, mais le serveur gardera la validation finale a la synchronisation.",
      reasons,
      actions,
      lot_watch_items: [],
      main_driver:
        !snapshotFresh && snapshotMeta?.generated_at
          ? `Le dernier snapshot local date du ${new Date(
              snapshotMeta.generated_at
            ).toLocaleString("fr-FR")}.`
          : "Les controles lots, FIFO et couts definitifs seront verifies cote serveur a la synchronisation.",
    },
    quick_actions: [],
  };
};

const buildOfflineProductionEstimate = (unitSnapshot, payload = {}) => {
  if (!unitSnapshot || !Array.isArray(unitSnapshot.materials)) {
    return null;
  }

  const requestedOutputQuantity = Number(payload?.output_quantity);
  if (!Number.isFinite(requestedOutputQuantity) || requestedOutputQuantity <= 0) {
    return null;
  }

  const snapshotMeta = unitSnapshot?.offline_snapshot || null;
  const snapshotGeneratedAt = snapshotMeta?.generated_at
    ? new Date(snapshotMeta.generated_at)
    : null;
  const snapshotGeneratedAtMs =
    snapshotGeneratedAt && !Number.isNaN(snapshotGeneratedAt.getTime())
      ? snapshotGeneratedAt.getTime()
      : null;
  const snapshotMaxAgeMinutes = Math.max(
    Number(snapshotMeta?.max_age_minutes) || 0,
    0
  );
  const snapshotFresh =
    snapshotGeneratedAtMs !== null &&
    snapshotMaxAgeMinutes > 0 &&
    Date.now() - snapshotGeneratedAtMs <= snapshotMaxAgeMinutes * 60 * 1000;

  const outputs =
    Array.isArray(unitSnapshot.outputs) && unitSnapshot.outputs.length > 0
      ? unitSnapshot.outputs
      : [
          {
            id: unitSnapshot.id || payload.production_unit_id || "offline-output",
            product_id: unitSnapshot.id || payload.production_unit_id || null,
            product_name:
              unitSnapshot.name ||
              `Unite #${payload.production_unit_id || "inconnue"}`,
            quantity: 1,
            unit_short_name: unitSnapshot.unit_short_name || "",
          },
        ];

  const baseOutputQuantity = Number(outputs[0]?.quantity) || 1;
  const ratio = requestedOutputQuantity / baseOutputQuantity;

  const calculatedMaterials = unitSnapshot.materials.map((material, index) => {
    const requiredQuantity = roundOfflineNumber(
      Number(material?.quantity || 0) * ratio
    );
    const purchasePrice = roundOfflineNumber(material?.purchase_price || 0);
    const rawCurrentStock = Number(material?.current_stock);
    const stockDataComplete = Number.isFinite(rawCurrentStock);
    const availableQuantity = stockDataComplete
      ? roundOfflineNumber(rawCurrentStock)
      : null;
    const estimatedRequiredCost = roundOfflineNumber(
      requiredQuantity * purchasePrice
    );

    return {
      ...material,
      id:
        material?.id ||
        `offline-material-${material?.product_id || index}-${requestedOutputQuantity}`,
      quantity: requiredQuantity,
      required_quantity: requiredQuantity,
      available: availableQuantity,
      current_stock: availableQuantity,
      purchase_price: purchasePrice,
      standard_purchase_price: purchasePrice,
      estimated_purchase_price: purchasePrice,
      required_cost: estimatedRequiredCost,
      standard_required_cost: estimatedRequiredCost,
      estimated_required_cost: estimatedRequiredCost,
      is_sufficient: stockDataComplete
        ? rawCurrentStock >= requiredQuantity
        : false,
      stock_data_complete: stockDataComplete,
      lot_tracking_enabled: false,
      lot_allocations_preview: [],
      lot_alerts: [],
      lot_allocation_complete: stockDataComplete
        ? rawCurrentStock >= requiredQuantity
        : false,
      lot_missing_quantity: stockDataComplete
        ? roundOfflineNumber(Math.max(requiredQuantity - rawCurrentStock, 0))
        : requiredQuantity,
      uses_legacy_lot: false,
    };
  });

  const calculatedVariableCosts = Array.isArray(unitSnapshot.variable_costs)
      ? unitSnapshot.variable_costs.map((variableCost, index) => {
        const unitCost = roundOfflineNumber(variableCost?.cost_per_unit || 0);
        const missingFields = [];
        if (!Number(variableCost?.supplier_id)) {
          missingFields.push("supplier_id");
        }
        if (!Number(variableCost?.expense_category_id)) {
          missingFields.push("expense_category_id");
        }
        return {
          ...variableCost,
          id:
            variableCost?.id ||
            `offline-variable-cost-${index}-${requestedOutputQuantity}`,
          cost_per_unit: unitCost,
          output_quantity: requestedOutputQuantity,
          total_cost: roundOfflineNumber(unitCost * requestedOutputQuantity),
          financial_configuration_complete: missingFields.length === 0,
          missing_financial_fields: missingFields,
          financial_status:
            unitCost * requestedOutputQuantity > 0
              ? missingFields.length === 0
                ? "ready"
                : "pending_configuration"
              : "not_applicable",
        };
      })
    : [];

  const calculatedOutputs = outputs.map((output, index) => ({
    ...output,
    id:
      output?.id ||
      `offline-output-${output?.product_id || index}-${requestedOutputQuantity}`,
    result_quantity: roundOfflineNumber(Number(output?.quantity || 0) * ratio),
  }));

  const stockDataComplete = calculatedMaterials.every(
    (material) => material.stock_data_complete
  );
  const allAvailable =
    stockDataComplete &&
    calculatedMaterials.every((material) => material.is_sufficient);

  const estimatedMaterialCost = roundOfflineNumber(
    calculatedMaterials.reduce(
      (sum, material) => sum + (Number(material.estimated_required_cost) || 0),
      0
    )
  );
  const totalVariableCost = roundOfflineNumber(
    calculatedVariableCosts.reduce(
      (sum, variableCost) => sum + (Number(variableCost.total_cost) || 0),
      0
    )
  );
  const estimatedTotalCost = roundOfflineNumber(
    estimatedMaterialCost + totalVariableCost
  );
  const estimatedUnitCost =
    requestedOutputQuantity > 0
      ? roundOfflineNumber(estimatedTotalCost / requestedOutputQuantity)
      : 0;
  const pendingFinancialItems = calculatedVariableCosts.filter(
    (item) => item.financial_status === "pending_configuration"
  );

  return {
    unit: unitSnapshot,
    materials: calculatedMaterials,
    variable_costs: calculatedVariableCosts,
    outputs: calculatedOutputs,
    all_available: allAvailable,
    output_quantity: requestedOutputQuantity,
    financial_tracking: {
      ready_count: calculatedVariableCosts.filter(
        (item) => item.financial_status === "ready"
      ).length,
      pending_regularization_count: pendingFinancialItems.length,
      pending_items: pendingFinancialItems.map((item) => ({
        source_variable_cost_id: Number(item.id) || null,
        label: item.label,
        amount: item.total_cost,
        missing_fields: item.missing_financial_fields,
      })),
      warning:
        pendingFinancialItems.length > 0
          ? `${pendingFinancialItems.length} charge(s) ne généreront pas de dépense et devront être régularisées après synchronisation.`
          : null,
    },
    recipe_summary: unitSnapshot.recipe_summary || null,
    pricing_guardrails: buildOfflineProductionGuardrails({
      recipeSummary: unitSnapshot.recipe_summary,
      allAvailable,
      stockDataComplete,
      snapshotFresh,
      snapshotMeta,
    }),
    cost_summary: {
      total_material_cost: estimatedMaterialCost,
      total_variable_cost: totalVariableCost,
      // Hors ligne : l'amortissement (plafond dependant de l'etat serveur) n'est
      // pas calcule ; le serveur le recalcule de maniere autoritaire a la synchro.
      total_depreciation_cost: 0,
      total_cost: estimatedTotalCost,
      unit_cost: estimatedUnitCost,
      unit_cost_real: estimatedUnitCost,
      standard_material_cost: estimatedMaterialCost,
      estimated_material_cost: estimatedMaterialCost,
      standard_total_cost: estimatedTotalCost,
      estimated_total_cost: estimatedTotalCost,
      standard_unit_cost: estimatedUnitCost,
      estimated_unit_cost: estimatedUnitCost,
      delta_amount: 0,
      delta_ratio: 0,
    },
    offline: true,
    cached: false,
    local_estimate: true,
    offline_warning: !snapshotFresh
      ? "Le snapshot offline de cet établissement a expire. Reconnectez-vous pour le rafraichir avant de produire hors ligne."
      : stockDataComplete
      ? "Calcul hors ligne reconstruit localement à partir de la configuration de l'unité et du dernier stock mémorisé."
      : "Impossible de valider le stock hors ligne: rechargez cette unite une fois en ligne pour memoriser les niveaux de stock.",
    offline_snapshot: snapshotMeta,
  };
};

const buildOfflineProductionCapacity = (unitSnapshot, payload = {}) => {
  if (
    !unitSnapshot ||
    !Array.isArray(unitSnapshot.materials) ||
    unitSnapshot.materials.length === 0
  ) {
    return null;
  }

  const outputs =
    Array.isArray(unitSnapshot.outputs) && unitSnapshot.outputs.length > 0
      ? unitSnapshot.outputs
      : [
          {
            product_id: unitSnapshot.id || payload.production_unit_id || null,
            product_name:
              unitSnapshot.name ||
              `Unite #${payload.production_unit_id || "inconnue"}`,
            quantity: 1,
            unit_short_name: unitSnapshot.unit_short_name || "",
          },
        ];
  const baseOutputQuantity = Number(outputs[0]?.quantity) || 1;
  const mode = payload?.mode === "material" ? "material" : "stock";
  const seenMaterialProductIds = new Set();
  const hasDuplicateMaterial = unitSnapshot.materials.some((material) => {
    const productId = Number(material?.product_id);
    if (!(productId > 0)) {
      return false;
    }
    if (seenMaterialProductIds.has(productId)) {
      return true;
    }
    seenMaterialProductIds.add(productId);
    return false;
  });

  if (hasDuplicateMaterial) {
    throw new Error(
      "Cette configuration contient plusieurs lignes pour le même composant. Corrigez-la avant de calculer sa capacité."
    );
  }

  const constraints = unitSnapshot.materials
    .map((material) => {
      const recipeQuantity = Number(material?.quantity);
      if (!Number.isFinite(recipeQuantity) || recipeQuantity <= 0) {
        return null;
      }

      const currentStock = Math.max(
        Number(material?.current_stock ?? material?.available) || 0,
        0
      );
      const usableCurrentStock = floorOfflineCapacity(currentStock);
      const rawStockOutputCapacity =
        (usableCurrentStock * baseOutputQuantity) / recipeQuantity;

      return {
        product_id: Number(material?.product_id) || null,
        product_name: material?.product_name || "Composant",
        unit_name: material?.unit_name || null,
        unit_short_name: material?.unit_short_name || null,
        recipe_quantity: roundOfflineNumber(recipeQuantity, 4),
        required_per_output: roundOfflineNumber(
          recipeQuantity / baseOutputQuantity,
          6
        ),
        current_stock: roundOfflineNumber(currentStock, 4),
        usable_current_stock: roundOfflineNumber(usableCurrentStock, 4),
        stock_output_capacity: floorOfflineCapacity(rawStockOutputCapacity),
        raw_stock_output_capacity: rawStockOutputCapacity,
      };
    })
    .filter(Boolean);

  if (constraints.length === 0) {
    return null;
  }

  const rawStockMaxOutputQuantity = Math.min(
    ...constraints.map((constraint) => constraint.raw_stock_output_capacity)
  );
  let selectedMaterial = null;
  let enteredMaterialQuantity = null;
  let rawTheoreticalOutputQuantity = rawStockMaxOutputQuantity;

  if (mode === "material") {
    selectedMaterial = constraints.find(
      (constraint) =>
        Number(constraint.product_id) === Number(payload.material_product_id)
    );
    enteredMaterialQuantity = Number(payload.material_quantity);

    if (
      !selectedMaterial ||
      !Number.isFinite(enteredMaterialQuantity) ||
      enteredMaterialQuantity <= 0
    ) {
      return null;
    }

    rawTheoreticalOutputQuantity =
      (floorOfflineCapacity(enteredMaterialQuantity) * baseOutputQuantity) /
      selectedMaterial.recipe_quantity;
  }

  const theoreticalOutputQuantity = floorOfflineCapacity(
    rawTheoreticalOutputQuantity
  );
  const stockMaxOutputQuantity = floorOfflineCapacity(
    rawStockMaxOutputQuantity
  );
  const isFullyAchievable =
    theoreticalOutputQuantity <= stockMaxOutputQuantity + 1e-9;
  const uncappedRawRecommendedOutputQuantity = Math.min(
    rawTheoreticalOutputQuantity,
    rawStockMaxOutputQuantity
  );
  const rawRecommendedOutputQuantity = Math.min(
    uncappedRawRecommendedOutputQuantity,
    MAX_PRODUCTION_OUTPUT_QUANTITY
  );
  const recommendedOutputQuantity = floorOfflineCapacity(
    rawRecommendedOutputQuantity
  );
  const isOutputCapped =
    uncappedRawRecommendedOutputQuantity >
    MAX_PRODUCTION_OUTPUT_QUANTITY + 1e-9;
  const stockLimitingMaterials = constraints.filter(
    (constraint) =>
      Math.abs(
        constraint.raw_stock_output_capacity - rawStockMaxOutputQuantity
      ) <= 1e-9
  );
  const limitingMaterials =
    mode === "material" && isFullyAchievable
      ? [
          {
            ...selectedMaterial,
            limiting_source: "entered_quantity",
            entered_quantity: roundOfflineNumber(
              enteredMaterialQuantity,
              4
            ),
          },
        ]
      : stockLimitingMaterials.map((constraint) => ({
          ...constraint,
          limiting_source: "current_stock",
        }));
  const preview =
    recommendedOutputQuantity > 0
      ? buildOfflineProductionEstimate(unitSnapshot, {
          ...payload,
          output_quantity: recommendedOutputQuantity,
        })
      : null;

  return {
    mode,
    output_step: 0.01,
    base_output_quantity: roundOfflineNumber(baseOutputQuantity, 4),
    theoretical_output_quantity: theoreticalOutputQuantity,
    stock_max_output_quantity: stockMaxOutputQuantity,
    uncapped_recommended_output_quantity: floorOfflineCapacity(
      uncappedRawRecommendedOutputQuantity
    ),
    recommended_output_quantity: recommendedOutputQuantity,
    is_fully_achievable: isFullyAchievable,
    is_output_capped: isOutputCapped,
    maximum_output_quantity: MAX_PRODUCTION_OUTPUT_QUANTITY,
    selected_material: selectedMaterial
      ? {
          ...selectedMaterial,
          entered_quantity: roundOfflineNumber(enteredMaterialQuantity, 4),
          entered_quantity_exceeds_stock:
            enteredMaterialQuantity > selectedMaterial.current_stock + 1e-9,
        }
      : null,
    limiting_materials: limitingMaterials.map(
      ({ raw_stock_output_capacity, ...material }) => material
    ),
    constraints: constraints.map(
      ({ raw_stock_output_capacity, ...constraint }) => constraint
    ),
    unit: {
      id: Number(unitSnapshot.id) || Number(payload.production_unit_id) || null,
      name: unitSnapshot.name || "Unite de production",
      production_kind: unitSnapshot.production_kind || "finished",
      image: unitSnapshot.image || null,
    },
    output: {
      product_id: Number(outputs[0]?.product_id) || null,
      product_name:
        outputs[0]?.product_name || unitSnapshot.name || "Produit fini",
      unit_name: outputs[0]?.unit_name || null,
      unit_short_name: outputs[0]?.unit_short_name || null,
    },
    preview,
    offline: true,
    local_estimate: true,
    offline_warning:
      "Capacite estimee a partir du dernier stock memorise. Elle sera reverifiee au retour de la connexion.",
  };
};

const enqueueOfflineProductionProcess = async (payload, offlinePreview = null) => {
  if (!payload?.recipe_version_hash) {
    throw new Error(
      "Le calcul valide de la configuration est requis avant un lancement hors ligne."
    );
  }

  const tempId = buildTempId("production-process");
  const preview = buildProductionPreviewFallback(payload, offlinePreview);

  await enqueueRequest({
    method: "POST",
    url: `${BASE_URL}/api/production/process`,
    body: {
      ...payload,
      client_request_id: tempId,
      offline_device_id: getOfflineProductionDeviceId(),
      performed_offline_at: new Date().toISOString(),
    },
    meta: {
      type: "production_process",
      tempId,
      preview,
    },
  });

  return {
    id: tempId,
    production_log_id: tempId,
    production_code: tempId,
    offline: true,
    queued: true,
    materials_used: preview.materials,
    variable_costs_applied: preview.variable_costs,
    financial_tracking: preview.financial_tracking,
    outputs_produced: preview.outputs,
    cost_summary: preview.cost_summary,
  };
};

// Récupérer toutes les unités de production avec pagination et filtres
export const getProductionUnits = async (params = {}) => {
  console.log("API call getProductionUnits with params:", params);

  // Make a copy to normalize parameters
  const apiParams = { ...params };

  // Ensure warehouse parameter is correctly named (try the most likely parameter name)
  if (params.warehouse_id && !params.warehouse) {
    apiParams.warehouse = params.warehouse_id;
  }

  console.log("Final API params:", apiParams);

  try {
    // Utiliser la nouvelle route pour la compatibilité
    const cacheKey = buildCacheKey("production:units", apiParams);
    const result = await fetchWithCache(
      cacheKey,
      async () => {
        try {
          const response = await axios.get(
            `${BASE_URL}/api/produits/production-units`,
            {
              params: apiParams,
            }
          );
          console.log("API response success:", response.data);
          return response.data;
        } catch (error) {
          console.log("Primary route failed, trying fallback route");
          const fallbackResponse = await axios.get(
            `${BASE_URL}/api/production/units`,
            {
              params: apiParams,
            }
          );
          console.log("Fallback API response success:", fallbackResponse.data);
          return fallbackResponse.data;
        }
      },
      { units: [], total: 0, pagination: null, filters: apiParams }
    );

    if (
      (!Array.isArray(result?.units) || result.units.length === 0) &&
      Number(apiParams?.warehouse_id) > 0 &&
      Number(apiParams?.company_id) > 0
    ) {
      const bootstrapPayload = await prefetchProductionOfflineBootstrap({
        warehouse_id: apiParams.warehouse_id,
        company_id: apiParams.company_id,
      });

      if (bootstrapPayload) {
        return buildProductionUnitsPayloadFromOfflineBootstrap(
          bootstrapPayload,
          apiParams
        );
      }
    }

    return result;
  } catch (error) {
    // Si la nouvelle route échoue, essayer l'ancienne route comme fallback
    if (
      Number(apiParams?.warehouse_id) > 0 &&
      Number(apiParams?.company_id) > 0
    ) {
      const bootstrapPayload = await prefetchProductionOfflineBootstrap({
        warehouse_id: apiParams.warehouse_id,
        company_id: apiParams.company_id,
      });

      if (bootstrapPayload) {
        return buildProductionUnitsPayloadFromOfflineBootstrap(
          bootstrapPayload,
          apiParams
        );
      }
    }

    console.error("Unable to load production units:", error);
    throw error.response?.data || error.message || error;
  }
};

// Récupérer une unité de production par ID
export const getProductionUnitById = async (id, params = {}) => {
  try {
    const cacheKey = buildCacheKey("production:unit:detail", { id, ...params });
    const data = await fetchWithCache(
      cacheKey,
      async () => {
        const response = await axios.get(
          `${BASE_URL}/api/production/units/${id}`,
          {
            params,
          }
        );
        return response.data;
      },
      null
    );

    if (!data) {
      const bootstrapUnit = await getProductionUnitFromOfflineBootstrap(id, params);

      if (bootstrapUnit) {
        return bootstrapUnit;
      }

      throw new Error("Aucune unite de production en cache pour ce contexte.");
    }

    return data;
  } catch (error) {
    const bootstrapUnit = await getProductionUnitFromOfflineBootstrap(id, params);

    if (bootstrapUnit) {
      return bootstrapUnit;
    }

    throw error.response?.data || error.message || error;
  }
};

export const getProductionOnboardingTemplates = async (params = {}) => {
  try {
    const response = await axios.get(
      `${BASE_URL}/api/production/onboarding/templates`,
      {
        params,
      }
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const getProductionOnboardingPreview = async (
  templateKey,
  params = {}
) => {
  try {
    const response = await axios.get(
      `${BASE_URL}/api/production/onboarding/preview/${templateKey}`,
      {
        params,
      }
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const applyProductionOnboardingTemplate = async (data) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/api/production/onboarding/apply`,
      data
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const getProductionOnboardingSuggestions = async (params = {}) => {
  try {
    const response = await axios.get(
      `${BASE_URL}/api/production/onboarding/suggestions`,
      {
        params,
      }
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const previewProductionOnboardingSuggestions = async (data) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/api/production/onboarding/suggestions/preview`,
      data
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const applyProductionOnboardingSuggestions = async (data) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/api/production/onboarding/suggestions/apply`,
      data
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

// Onboarding par import de menu (photo/PDF). Action EN LIGNE uniquement (pas de
// cache offline) : envoie le fichier, recoit les correspondances trouvees.
export const scanProductionMenu = async (formData, params = {}) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/api/production/onboarding/menu-scan`,
      formData,
      {
        params,
        headers: { "Content-Type": "multipart/form-data" },
      }
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const previewProductionOnboardingMenu = async (data) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/api/production/onboarding/menu/preview`,
      data
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const applyProductionOnboardingMenu = async (data) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/api/production/onboarding/menu/apply`,
      data
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const suggestProductionUnitPrice = async (data) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/api/production/units/price-suggestion`,
      data,
      {
        headers: { "Content-Type": "application/json" },
      }
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message || error;
  }
};

// Moteur d'assistance recettes (local) : recettes similaires par nom.
// Tolerant aux pannes/offline : renvoie toujours un objet sur, jamais d'erreur.
export const getRecipeSuggestions = async (params = {}) => {
  const fallback = { eligible: false, suggestions: [] };
  try {
    return await fetchWithCache(
      buildCacheKey("production:recipe-suggestions", params),
      async () => {
        const response = await axios.get(
          `${BASE_URL}/api/production/recipe-suggestions`,
          { params }
        );
        return response.data;
      },
      fallback
    );
  } catch (error) {
    return fallback;
  }
};

// Moteur d'assistance recettes (local) : quantite calibree pour un ingredient.
export const getIngredientSuggestions = async (params = {}) => {
  const fallback = { eligible: false, suggestions: [] };
  try {
    return await fetchWithCache(
      buildCacheKey("production:ingredient-suggestions", params),
      async () => {
        const response = await axios.get(
          `${BASE_URL}/api/production/ingredient-suggestions`,
          { params }
        );
        return response.data;
      },
      fallback
    );
  } catch (error) {
    return fallback;
  }
};

// Moteur d'assistance recettes (local) : ingredients souvent ajoutes (manquants)
// dans des recettes au nom similaire. Tolerant aux pannes/offline.
export const getMissingIngredientSuggestions = async (params = {}) => {
  const fallback = { eligible: false, suggestions: [] };
  try {
    return await fetchWithCache(
      buildCacheKey("production:missing-ingredients", params),
      async () => {
        const response = await axios.get(
          `${BASE_URL}/api/production/recipe-suggestions/missing-ingredients`,
          { params }
        );
        return response.data;
      },
      fallback
    );
  } catch (error) {
    return fallback;
  }
};

// Reglages globaux du moteur d'assistance recettes (SysAdmin uniquement).
// Lecture : laisse remonter l'erreur (403/500) pour que la page l'affiche.
export const getRecipeAssistantSettings = async () => {
  const response = await axios.get(
    `${BASE_URL}/api/production/recipe-assistant/settings`
  );
  return response.data;
};

// Enregistrement des reglages (SysAdmin uniquement).
export const putRecipeAssistantSettings = async (payload = {}) => {
  const response = await axios.put(
    `${BASE_URL}/api/production/recipe-assistant/settings`,
    payload
  );
  return response.data;
};

// Créer une nouvelle unité de production
export const createProductionUnit = async (data) => {
  try {
    // Si les données sont au format FormData, extraire et convertir les données spécifiques
    let dataToSend = data;
    let hasImage = false;
    let imageFile = null;

    if (data instanceof FormData) {
      console.log("Traitement du FormData pour le serveur");

      // Vérifier si tous les champs requis sont présents
      const requiredFields = [
        "name",
        "company_id",
        "warehouse_id",
        "unit_id",
        "category_id",
      ];
      for (const field of requiredFields) {
        if (!data.get(field)) {
          throw new Error(`Le champ ${field} est requis`);
        }
      }

      // Ajouter la date et l'heure actuelles
      const currentDateTime = new Date().toISOString();
      data.append("created_at", currentDateTime);
      data.append("updated_at", currentDateTime);

      // Gérer l'image
      if (data.get("image") && data.get("image").size > 0) {
        hasImage = true;
        imageFile = data.get("image");
        console.log("Image détectée dans FormData:", imageFile.name);
      }

      // Valider les matières premières
      let materialsValid = false;
      if (data.get("materials")) {
        try {
          const materialsData = JSON.parse(data.get("materials"));
          if (Array.isArray(materialsData) && materialsData.length > 0) {
            materialsValid = materialsData.some(
              (m) => m.product_id && m.quantity > 0
            );
          }
        } catch (e) {
          console.error("Erreur lors de la validation des materials:", e);
        }
      }

      if (!materialsValid) {
        throw new Error(
          "Veuillez ajouter au moins une matière première valide"
        );
      }

      // Assurer que les champs numériques sont correctement formatés
      [
        "status",
        "stock_quantitiy_alert",
        "sales_price",
        "target_margin_rate",
        "output_quantity",
      ].forEach((key) => {
        if (data.has(key)) {
          const value = data.get(key);
          const numericValue = Number(value);
          if (!isNaN(numericValue)) {
            // Remplacer la valeur par la version numérique
            data.set(key, numericValue);
          }
        }
      });

      // Si nous utilisons FormData avec une image, nous pouvons l'envoyer directement
      // car le serveur est configuré pour traiter multipart/form-data
      if (hasImage) {
        console.log("Envoi du formulaire avec image via multipart/form-data");
        // Nous envoyons directement le FormData
        const response = await axios.post(
          `${BASE_URL}/api/production/units`,
          data,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );
        return response.data;
      } else {
        // S'il n'y a pas d'image, nous pouvons toujours utiliser FormData
        // mais nous pouvons aussi convertir en JSON pour plus de fiabilité
        console.log("Conversion du FormData en JSON (pas d'image)");

        // Créer un objet JSON à partir du FormData
        const jsonData = {};
        for (let [key, value] of data.entries()) {
          if (key !== "materials" && key !== "outputs" && key !== "image") {
            // Convertir les valeurs numériques en nombres
            if (
              !isNaN(value) &&
              key !== "name" &&
              key !== "description" &&
              key !== "slug" &&
              key !== "barcode_symbology" &&
              key !== "item_code" &&
              key !== "created_at" &&
              key !== "updated_at"
            ) {
              jsonData[key] = Number(value);
            } else {
              jsonData[key] = value;
            }
          }
        }

        // Traiter les matériaux en JSON
        if (data.get("materials")) {
          try {
            jsonData.materials = JSON.parse(data.get("materials"));
            console.log("Materials parsés:", jsonData.materials);
          } catch (e) {
            console.error("Erreur lors du parsing des materials:", e);
            throw new Error("Format de matériaux invalide");
          }
        }

        // Traiter les sorties en JSON
        if (data.get("outputs")) {
          try {
            jsonData.outputs = JSON.parse(data.get("outputs"));
            console.log("Outputs parsés:", jsonData.outputs);

            // Vérifier et s'assurer que outputs est bien un tableau
            if (!Array.isArray(jsonData.outputs)) {
              console.error("Outputs n'est pas un tableau:", jsonData.outputs);
              // Forcer à être un tableau si nécessaire
              jsonData.outputs = [];
            }
          } catch (e) {
            console.error("Erreur lors du parsing des outputs:", e);
            jsonData.outputs = [];
          }
        } else {
          jsonData.outputs = [];
        }

        // S'assurer qu'il y a au moins un élément de sortie
        if (jsonData.outputs.length === 0) {
          // Ajouter un élément factice pour satisfaire le backend
          jsonData.outputs.push({
            product_id: "self_reference",
            quantity: Number(data.get("output_quantity")) || 1,
          });
        }

        dataToSend = jsonData;
      }
    }

    console.log("Données finales à envoyer:", dataToSend);

    // Envoyer les données
    const response = await axios.post(
      `${BASE_URL}/api/production/units`,
      dataToSend,
      {
        headers:
          dataToSend instanceof FormData
            ? { "Content-Type": "multipart/form-data" }
            : { "Content-Type": "application/json" },
      }
    );

    return response.data;
  } catch (error) {
    console.error(
      "Erreur lors de la création de l'unité de production:",
      error
    );

    // Si nous avons une réponse du serveur avec des données d'erreur
    if (error.response && error.response.data) {
      console.error("Détails de l'erreur serveur:", error.response.data);
      throw error.response.data;
    }

    // Si c'est une erreur que nous avons générée nous-mêmes
    if (error.message) {
      throw { error: error.message };
    }

    // Erreur générique
    throw { error: "Erreur lors de la création de l'unité de production" };
  }
};

// Mettre à jour une unité de production
export const updateProductionUnit = async (id, data) => {
  try {
    // Ajouter la date et l'heure actuelles pour la mise à jour
    if (data instanceof FormData) {
      data.append("updated_at", new Date().toISOString());
    } else {
      data.updated_at = new Date().toISOString();
    }

    const response = await axios.put(
      `${BASE_URL}/api/production/units/${id}`,
      data
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

// Supprimer une unité de production
export const duplicateProductionUnit = async (id) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/api/production/units/${id}/duplicate`
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const deleteProductionUnit = async (id) => {
  try {
    const response = await axios.delete(
      `${BASE_URL}/api/production/units/${id}`
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

// ===========================================================================
// Materiels de production (amortissement -> cout de revient)
// ===========================================================================
export const getProductionEquipment = async (params = {}) => {
  try {
    const response = await axios.get(`${BASE_URL}/api/production/equipment`, {
      params,
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const createProductionEquipment = async (data) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/api/production/equipment`,
      data
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const updateProductionEquipment = async (id, data) => {
  try {
    const response = await axios.put(
      `${BASE_URL}/api/production/equipment/${id}`,
      data
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const deleteProductionEquipment = async (id, params = {}) => {
  try {
    const response = await axios.delete(
      `${BASE_URL}/api/production/equipment/${id}`,
      { params }
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

// Calculer les besoins en matières premières pour une production
export const calculateProductionNeeds = async (data) => {
  let fallbackEstimate = null;

  try {
    const {
      __offline_unit_snapshot: offlineUnitSnapshot = null,
      ...payload
    } = data || {};
    if (
      Number(payload?.output_quantity) > MAX_PRODUCTION_OUTPUT_QUANTITY
    ) {
      throw new Error(
        `La quantite maximale par lancement est de ${MAX_PRODUCTION_OUTPUT_QUANTITY}. Fractionnez cette production en plusieurs lancements.`
      );
    }

    const cacheKey = buildCacheKey("production:calculate", {
      production_unit_id: Number(payload?.production_unit_id) || null,
      output_quantity: Number(payload?.output_quantity) || null,
      warehouse_id: Number(payload?.warehouse_id) || null,
      company_id: Number(payload?.company_id) || null,
    });
    const resolvedOfflineUnitSnapshot = await resolveProductionOfflineUnitSnapshot(
      offlineUnitSnapshot,
      payload
    );
    fallbackEstimate = buildOfflineProductionEstimate(
      resolvedOfflineUnitSnapshot,
      payload
    );

    if (isOffline()) {
      if (fallbackEstimate) {
        return fallbackEstimate;
      }

      throw new Error(
        "Le calcul hors ligne n'est pas encore possible pour cette unite. Rechargez le module Production en ligne pour initialiser le snapshot local."
      );
    }

    const response = await axios.post(
      `${BASE_URL}/api/production/calculate`,
      payload
    );
    const result = response.data;
    await cacheSet(cacheKey, result);

    if (!result) {
      throw new Error(
        "Erreur lors du calcul des besoins en composants"
      );
    }

    return result;
  } catch (error) {
    if (isLikelyNetworkError(error) && fallbackEstimate) {
      return fallbackEstimate;
    }

    throw error.response?.data || error.message || error;
  }
};

// Calculer de facon autoritaire les besoins cumules d'un lot de productions.
// L'execution batch et le mode hors ligne seront introduits dans une phase
// ulterieure : ce calcul doit donc toujours etre confirme par le serveur.
export const calculateProductionBatchNeeds = async (data) => {
  try {
    const items = Array.isArray(data?.items) ? data.items : [];
    const oversizedItem = items.find(
      (item) =>
        Number(item?.output_quantity) > MAX_PRODUCTION_OUTPUT_QUANTITY,
    );
    if (oversizedItem) {
      throw new Error(
        `La quantite maximale par lancement est de ${MAX_PRODUCTION_OUTPUT_QUANTITY}. Fractionnez cette production en plusieurs lancements.`,
      );
    }

    if (isOffline()) {
      throw new Error(
        "Le calcul d'un lot de productions necessite une connexion afin de verifier le stock partage et les couts FIFO.",
      );
    }

    const response = await axios.post(
      `${BASE_URL}/api/production/batch/calculate`,
      data,
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message || error;
  }
};

export const processProductionBatch = async (data) => {
  try {
    const items = Array.isArray(data?.items) ? data.items : [];
    const oversizedItem = items.find(
      (item) =>
        Number(item?.output_quantity) > MAX_PRODUCTION_OUTPUT_QUANTITY,
    );
    if (oversizedItem) {
      throw new Error(
        `La quantite maximale par lancement est de ${MAX_PRODUCTION_OUTPUT_QUANTITY}. Fractionnez cette production en plusieurs lancements.`,
      );
    }

    if (isOffline()) {
      throw new Error(
        "Le lancement d'un lot de productions necessite une connexion. Aucun stock n'a ete modifie.",
      );
    }

    const response = await axios.post(
      `${BASE_URL}/api/production/batch/process`,
      data,
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message || error;
  }
};

export const calculateProductionCapacity = async (data) => {
  let fallbackCapacity = null;

  try {
    const {
      __offline_unit_snapshot: offlineUnitSnapshot = null,
      ...payload
    } = data || {};
    const resolvedOfflineUnitSnapshot =
      await resolveProductionOfflineUnitSnapshot(
        offlineUnitSnapshot,
        payload
      );
    fallbackCapacity = buildOfflineProductionCapacity(
      resolvedOfflineUnitSnapshot,
      payload
    );

    if (isOffline()) {
      if (fallbackCapacity) {
        return fallbackCapacity;
      }

      throw new Error(
        "Le calcul inverse hors ligne n'est pas disponible pour cette unite. Rechargez-la une fois en ligne."
      );
    }

    const response = await axios.post(
      `${BASE_URL}/api/production/capacity`,
      payload
    );
    return response.data;
  } catch (error) {
    if (isLikelyNetworkError(error) && fallbackCapacity) {
      return fallbackCapacity;
    }

    throw error.response?.data || error.message || error;
  }
};

// Initier une production
export const processProduction = async (data) => {
  const { __offline_preview: offlinePreview = null, ...payload } = data || {};

  try {
    if (
      Number(payload?.output_quantity) > MAX_PRODUCTION_OUTPUT_QUANTITY
    ) {
      throw new Error(
        `La quantite maximale par lancement est de ${MAX_PRODUCTION_OUTPUT_QUANTITY}. Fractionnez cette production en plusieurs lancements.`
      );
    }

    if (isOffline()) {
      return await enqueueOfflineProductionProcess(payload, offlinePreview);
    }

    const response = await axios.post(
      `${BASE_URL}/api/production/process`,
      payload
    );
    return response.data;
  } catch (error) {
    if (isLikelyNetworkError(error)) {
      return enqueueOfflineProductionProcess(payload, offlinePreview);
    }

    throw error.response?.data || error.message || error;
  }
};

export const getRestaurantKitchenQueue = async (params = {}) => {
  try {
    const response = await axios.get(`${BASE_URL}/api/restaurant/kitchen/queue`, {
      params,
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message || error;
  }
};

export const markRestaurantKitchenTicketReady = async (ticketId, data = {}) => {
  try {
    const response = await axios.post(`${BASE_URL}/api/restaurant/kitchen/tickets/${ticketId}/ready`, data);
    return response.data;
  } catch (error) { throw error.response?.data || error.message || error; }
};

export const updateRestaurantKitchenTicketStatus = async (ticketId, data = {}) => {
  try {
    const response = await axios.patch(
      `${BASE_URL}/api/restaurant/kitchen/tickets/${ticketId}/status`,
      data
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message || error;
  }
};

export const getRestaurantBootstrap = async (params = {}) => {
  try {
    const response = await axios.get(`${BASE_URL}/api/restaurant/bootstrap`, {
      params,
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message || error;
  }
};

export const getRestaurantOrders = async (params = {}) => {
  try {
    const response = await axios.get(`${BASE_URL}/api/restaurant/orders`, {
      params,
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message || error;
  }
};

export const getRestaurantOrder = async (orderId, params = {}) => {
  try {
    const response = await axios.get(
      `${BASE_URL}/api/restaurant/orders/${orderId}`,
      { params }
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message || error;
  }
};

export const createRestaurantOrder = async (payload = {}) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/api/restaurant/orders`,
      payload
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message || error;
  }
};

export const updateRestaurantOrder = async (orderId, payload = {}) => {
  try {
    const response = await axios.put(
      `${BASE_URL}/api/restaurant/orders/${orderId}`,
      payload
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message || error;
  }
};

export const deleteRestaurantOrder = async (orderId, params = {}) => {
  try {
    const response = await axios.delete(
      `${BASE_URL}/api/restaurant/orders/${orderId}`,
      { params }
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message || error;
  }
};

export const updateRestaurantOrderStatus = async (orderId, payload = {}) => {
  try {
    const response = await axios.patch(
      `${BASE_URL}/api/restaurant/orders/${orderId}/status`,
      payload
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message || error;
  }
};

export const sendRestaurantOrderToKitchen = async (orderId, payload = {}) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/api/restaurant/orders/${orderId}/send-to-kitchen`,
      payload
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message || error;
  }
};

// Convertit une commande restaurant (staff ou self/QR) en vente POS et genere
// le ticket de caisse. payload : { company_id, warehouse_id?, payment: { amount,
// payment_mode_id, treasury_account_id?, date?, notes? } }
export const convertRestaurantOrderToSale = async (orderId, payload = {}) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/api/restaurant/orders/${orderId}/convert-to-sale`,
      payload
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message || error;
  }
};

export const getRestaurantDigitalMenu = async (params = {}, options = {}) => {
  try {
    const response = await axios.get(`${BASE_URL}/api/restaurant/digital-menu`, {
      params,
      signal: options.signal,
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message || error;
  }
};

export const saveRestaurantDigitalMenu = async (payload = {}) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/api/restaurant/digital-menu`,
      payload
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message || error;
  }
};

export const getRestaurantDigitalMenuTables = async (params = {}) => {
  try {
    const response = await axios.get(
      `${BASE_URL}/api/restaurant/digital-menu/tables`,
      { params }
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message || error;
  }
};

export const createRestaurantDigitalMenuTable = async (payload = {}) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/api/restaurant/digital-menu/tables`,
      payload
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message || error;
  }
};

export const updateRestaurantDigitalMenuTable = async (tableId, payload = {}) => {
  try {
    const response = await axios.patch(
      `${BASE_URL}/api/restaurant/digital-menu/tables/${tableId}`,
      payload
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message || error;
  }
};

export const deactivateRestaurantDigitalMenuTable = async (tableId, params = {}) => {
  try {
    const response = await axios.delete(
      `${BASE_URL}/api/restaurant/digital-menu/tables/${tableId}`,
      { params }
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message || error;
  }
};

export const getRestaurantCatalogProducts = async (params = {}) => {
  try {
    const response = await axios.get(`${BASE_URL}/api/produits`, {
      params: {
        page: 1,
        limit: 200,
        sellableOnly: "true",
        ...params,
      },
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message || error;
  }
};

// Obtenir l'historique des productions
export const getProductionHistory = async (
  page = 1,
  limit = 10,
  filters = {},
  requestOptions = {}
) => {
  try {
    const params = { page, limit };

    // Ajouter les filtres optionnels
    if (filters.warehouse_id) {
      params.warehouse_id = filters.warehouse_id;
    }

    if (filters.company_id) {
      params.company_id = filters.company_id;
    }

    if (filters.start_date) {
      params.start_date = filters.start_date;
    }

    if (filters.end_date) {
      params.end_date = filters.end_date;
    }

    console.log("API call getProductionHistory with params:", params);

    const cached = await fetchWithCacheMetadata(
      buildCacheKey("production:history", params),
      async () => {
        const response = await axios.get(`${BASE_URL}/api/production/history`, {
          params,
          signal: requestOptions.signal,
        });
        return response.data;
      },
      { logs: [], total: 0, pagination: null, filters: params }
    );
    const payload = cached.data;

    const pendingLogs = await getPendingProductionLogsFromQueue(filters);
    return {
      ...payload,
      logs: [...pendingLogs, ...(payload?.logs || [])],
      total: Number(payload?.total || 0) + pendingLogs.length,
      ...(requestOptions.withMetadata
        ? { freshness: { status: cached.status, updatedAt: cached.updatedAt } }
        : {}),
    };
  } catch (error) {
    if (requestOptions.signal?.aborted) throw error;
    throw error.response?.data || error.message;
  }
};

// Obtenir le suivi des coûts de production (snapshots)
export const getProductionCostTracking = async (
  page = 1,
  limit = 20,
  filters = {},
  requestOptions = {}
) => {
  try {
    const params = { page, limit };

    if (filters.warehouse_id) {
      params.warehouse_id = filters.warehouse_id;
    }

    if (filters.company_id) {
      params.company_id = filters.company_id;
    }

    if (filters.start_date) {
      params.start_date = filters.start_date;
    }

    if (filters.end_date) {
      params.end_date = filters.end_date;
    }

    if (filters.include_deleted !== undefined) {
      params.include_deleted = filters.include_deleted;
    }

    const cached = await fetchWithCacheMetadata(
      buildCacheKey("production:cost-tracking", params),
      async () => {
        const response = await axios.get(
          `${BASE_URL}/api/production/cost-tracking`,
          {
            params,
            signal: requestOptions.signal,
          }
        );
        return response.data;
      },
      {
        logs: [],
        total: 0,
        pagination: null,
        summary: null,
        by_unit: [],
        monthly_trend: [],
        filters: params,
      }
    );
    return {
      ...cached.data,
      ...(requestOptions.withMetadata
        ? { freshness: { status: cached.status, updatedAt: cached.updatedAt } }
        : {}),
    };
  } catch (error) {
    if (requestOptions.signal?.aborted) throw error;
    throw error.response?.data || error.message;
  }
};

// Obtenir les détails complets d'une production par ID
export const getProductionDetails = async (productionId, params = {}, requestOptions = {}) => {
  try {
    console.log("API call getProductionDetails for ID:", productionId);

    const fetchDetails = async () => {
      const response = await axios.get(
        `${BASE_URL}/api/production/history/${productionId}`,
        { params, signal: requestOptions.signal }
      );
      return response.data;
    };
    if (requestOptions.networkOnly) return await fetchDetails();

    const cached = await fetchWithCacheMetadata(
      buildCacheKey("production:history:detail", {
        productionId,
        ...params,
      }),
      fetchDetails,
      null
    );
    const data = cached.data;

    if (!data) {
      throw new Error(
        "Aucun detail de production en cache pour cet enregistrement."
      );
    }

    return requestOptions.withMetadata
      ? { ...data, freshness: { status: cached.status, updatedAt: cached.updatedAt } }
      : data;
  } catch (error) {
    if (requestOptions.signal?.aborted) throw error;
    throw error.response?.data || error.message;
  }
};

export const regularizeProductionVariableCost = async (
  productionId,
  variableCostLogId,
  data = {}
) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/api/production/history/${productionId}/variable-costs/${variableCostLogId}/regularize-expense`,
      data
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

// Récupérer tous les produits (pour les sélecteurs)
// Annuler (supprimer) une production
export const deleteProductionLog = async (
  productionId,
  reason = "",
  params = {}
) => {
  try {
    const response = await axios.delete(
      `${BASE_URL}/api/production/history/${productionId}`,
      { data: { reason }, params }
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const declareProductionLotLoss = async (
  productionId,
  lotId,
  data = {}
) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/api/production/history/${productionId}/lots/${lotId}/losses`,
      data
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const revalueProductionLot = async (lotId, data = {}) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/api/production/lot-watch/${lotId}/revalue`,
      data
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

// Recettes "vendables sans production" en stock negatif a regulariser (par magasin).
// La regularisation elle-meme passe par le flux de production standard
// (POST /process avec is_regularization), pour valider la consommation des
// matieres et voir les manquants eventuels.
export const getRegularizationPending = async (params = {}) => {
  try {
    const response = await axios.get(
      `${BASE_URL}/api/production/regularization/pending`,
      { params }
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message || error;
  }
};

export const getProducts = async (
  search = "",
  warehouse = null,
  type = "raw",
  extraParams = {}
) => {
  try {
    const params = { search, ...extraParams };
    if (warehouse) params.warehouse = warehouse;
    if (type) params.type = type;

    const response = await axios.get(`${BASE_URL}/api/produits`, { params });
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};
